import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { areSliderStatesEqual, SLIDER_INITIAL_STATE } from './cost-to-build-client-ui-slider.state';
import type { SliderState } from './cost-to-build-client-ui-slider.types';

const formatSquareMeters = (value: number) => `${value} m²`;

const getDecimalPlaces = (value: number) => {
    const normalizedValue = Number.isFinite(value) ? value : 0;
    const fractionPart = normalizedValue.toString().split('.')[1];

    return fractionPart ? fractionPart.length : 0;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const normalizeStep = (step: number) => (Number.isFinite(step) && step > 0 ? step : 1);

const normalizeBounds = (min: number, max: number) => {
    const safeMin = Number.isFinite(min) ? min : SLIDER_INITIAL_STATE.min;
    const safeMax = Number.isFinite(max) ? max : safeMin;

    return {
        max: Math.max(safeMax, safeMin),
        min: safeMin,
    };
};

const roundToNearestStep = (value: number, min: number, step: number) => {
    const precision = Math.max(getDecimalPlaces(min), getDecimalPlaces(step), getDecimalPlaces(value));
    const roundedValue = min + Math.round((value - min) / step) * step;

    return Number(roundedValue.toFixed(precision));
};

const normalizeSliderState = (state: SliderState): SliderState => {
    const { max, min } = normalizeBounds(state.min, state.max);
    const step = normalizeStep(state.step);
    const safeValue = Number.isFinite(state.value) ? state.value : min;
    const roundedValue = roundToNearestStep(safeValue, min, step);

    return {
        ...state,
        max,
        min,
        step,
        tooltipVisible: state.tooltipVisible,
        value: clamp(roundedValue, min, max),
    };
};

export const SliderStore = signalStore(
    withState(SLIDER_INITIAL_STATE),
    withComputed((store) => ({
        formattedMax: computed(() => formatSquareMeters(store.max())),
        formattedMin: computed(() => formatSquareMeters(store.min())),
        formattedValue: computed(() => formatSquareMeters(store.value())),
        progressPercent: computed(() => {
            const range = store.max() - store.min();

            if (range < 0) {
                return 0;
            }

            return ((store.value() - store.min()) / range) * 100;
        }),
        resolvedAriaLabel: computed(() => store.ariaLabel() || store.label() || formatSquareMeters(store.value())),
        state: computed<SliderState>(() => ({
            ariaLabel: store.ariaLabel(),
            id: store.id(),
            label: store.label(),
            max: store.max(),
            min: store.min(),
            step: store.step(),
            tooltipVisible: store.tooltipVisible(),
            value: store.value(),
        })),
    })),
    withMethods((store) => {
        const setState = (patch: Partial<SliderState>) => {
            const nextState = normalizeSliderState({
                ...store.state(),
                ...patch,
            });

            if (areSliderStatesEqual(store.state(), nextState)) {
                return;
            }

            patchState(store, nextState);
        };

        return {
            setState,
            setTooltipVisible(tooltipVisible: boolean) {
                setState({ tooltipVisible });
            },
            setValue(value: number) {
                setState({ value });
            },
        };
    })
);
